// Classic's states (ARCHITECTURE §6.4 items 7, 11, 12).
//
// - RouteLoading: a verbatim copy of today's PageLoader. (Classic has no
//   Frame, so its lazy pages actually suspend the app-level boundary, whose
//   fallback is the identical core BootLoader; this is the declared slot.)
// - PageError / NotFound: Classic mounts its own chrome per page, so these
//   render the nav and footer around a centred message in the same language
//   as TeamDetail's "not found" state (#312: they used to render nothing, so
//   an unknown URL or a page that threw was a blank screen). Friendly copy
//   only, never the raw error (#420).

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { sf } from '../../core/features/sf'
import { useLabels } from '../../core/labels'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import Navigation from './components/Navigation'
import LeagueFooter from './components/LeagueFooter'

export function RouteLoading() {
  const t = useLabels()
  return (
    <div className="min-h-screen bg-gradient-to-b from-bg-secondary via-bg-primary to-bg-primary flex items-center justify-center">
      <div className="text-white/60 text-sm">{t.ui.common.loading}</div>
    </div>
  )
}

function StateScreen({ title, body, feature, action }: { title: string; body: string; feature: string; action: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-bg-secondary via-bg-primary to-bg-primary">
      <Navigation />
      <div className="flex-1 container mx-auto px-4 py-20" {...sf(feature)}>
        <div className="max-w-lg mx-auto text-center">
          <h1 className="text-2xl font-['Montserrat'] font-black text-white uppercase tracking-tight mb-2">{title}</h1>
          <p className="text-white/50 mb-8">{body}</p>
          {action}
        </div>
      </div>
      <LeagueFooter />
    </div>
  )
}

const ACTION = 'inline-flex items-center gap-2 px-6 py-3 bg-secondary/30 border border-secondary text-white hover:bg-secondary/50 hover:border-primary/50 transition-all text-sm font-bold uppercase tracking-wider'

export function PageError({ reset }: { error: Error; reset: () => void }) {
  const t = useLabels()
  return (
    <StateScreen
      title={t.ui.common.errorTitle}
      body={t.ui.common.errorMessage(undefined)}
      feature="state.error"
      action={<button type="button" onClick={reset} className={ACTION}>{t.ui.common.retry}</button>}
    />
  )
}

export function NotFound() {
  const t = useLabels()
  const c = useT(siteClassicMessages).chrome
  return (
    <StateScreen
      title={t.ui.common.notFoundTitle}
      body={t.ui.common.notFoundBody}
      feature="state.not-found"
      action={<Link to="/" className={ACTION}>{c.backHome}</Link>}
    />
  )
}
